"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ArrowLeft,
  Building2,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  User,
} from "lucide-react";
import { FormEvent, useState, useSyncExternalStore, useEffect } from "react";
import { captureRegistrationIntent, selectedPlanPath } from "@/lib/registration-intent";
import { getAccessToken } from "@/lib/storage";
import { getCurrentUser } from "@/lib/auth";

import Logo from "@/components/Logo";
import GoogleAuthButton from "@/components/auth/GoogleAuthButton";
import { ApiError } from "@/lib/api";
import {
  authenticateWithGoogle,
  registerUser,
} from "@/lib/auth";
import type { ApiValidationError } from "@/types/auth";

function getApiErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return "Não foi possível criar sua conta. Tente novamente.";
  }

  const data = error.data;

  if (!data || typeof data !== "object") {
    return error.message;
  }

  const validationErrors = data as ApiValidationError;

  const fieldLabels: Record<string, string> = {
    username: "E-mail",
    email: "E-mail",
    first_name: "Nome",
    last_name: "Sobrenome",
    password: "Senha",
    password_confirm: "Confirmação de senha",
    non_field_errors: "Cadastro",
  };

  for (const [field, value] of Object.entries(validationErrors)) {
    const label = fieldLabels[field] ?? field;

    if (typeof value === "string") {
      return `${label}: ${value}`;
    }

    if (Array.isArray(value) && value.length > 0) {
      return `${label}: ${String(value[0])}`;
    }
  }

  return error.message;
}

export default function RegisterForm() {
  const router = useRouter();
  useEffect(() => {
    captureRegistrationIntent(window.location.search);
    if (getAccessToken()) void getCurrentUser().then(user => {
      if (user.has_company) router.replace(selectedPlanPath());
    }).catch(() => {});
  }, [router]);

  const registerType = useSyncExternalStore(
    () => () => {},
    () => {
      const value = window.sessionStorage.getItem(
        "register-type",
      );

      return value === "pessoa-fisica"
        ? "pessoa-fisica"
        : "empresa";
    },
    () => "empresa",
  );

  const isIndividual =
    registerType === "pessoa-fisica";

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");


  async function handleGoogleCredential(
    credential: string,
  ) {
    if (isSubmitting) {
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const response =
        await authenticateWithGoogle({
          credential,
          registration_type: isIndividual
            ? "individual"
            : "company",
        });

      if ("two_factor_required" in response) {
        router.replace("/login");
        return;
      }

      const user = response.user;

      if (user.has_company) {
        router.replace(selectedPlanPath());
        return;
      }

      router.replace(
        user.registration_type === "individual"
          ? "/pessoa-fisica/cadastro"
          : "/empresa/cadastro",
      );
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setErrorMessage("");

    const formData = new FormData(event.currentTarget);

    const firstName = String(formData.get("firstName") ?? "").trim();
    const lastName = String(formData.get("lastName") ?? "").trim();
    const email = String(formData.get("email") ?? "")
      .trim()
      .toLowerCase();
    const password = String(formData.get("password") ?? "");
    const passwordConfirmation = String(
      formData.get("passwordConfirmation") ?? "",
    );

    if (password !== passwordConfirmation) {
      setErrorMessage("As senhas não coincidem.");
      return;
    }

    if (password.length < 8) {
      setErrorMessage("A senha deve ter pelo menos 8 caracteres.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await registerUser({
        username: email,
        email,
        first_name: firstName,
        last_name: lastName,
        registration_type: isIndividual
          ? "individual"
          : "company",
        password,
        password_confirm: passwordConfirmation,
      });

      const searchParams = new URLSearchParams({
        email,
        sent: String(response.verification_email_sent),
        message: response.message,
      });

      router.replace(
        `/verificar-email?${searchParams.toString()}`,
      );
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full bg-slate-50">
      <section className="flex w-full items-center justify-center px-5 py-8 lg:w-1/2 lg:px-12">
        <div className="w-full max-w-[430px]">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600"
          >
            <ArrowLeft size={17} />
            Voltar
          </Link>

          <div className="mt-6">
            <Logo compact />
          </div>

          <div className="mt-7">
            <h1 className="text-2xl font-bold tracking-tight text-slate-950">
              Crie sua conta
            </h1>

            <p className="mt-2 text-base text-slate-500">
              Cadastre-se para criar e administrar sua loja.
            </p>
          </div>

          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600">
            {isIndividual ? <User size={14} /> : <Building2 size={14} />}

            {isIndividual
              ? "Cadastro de pessoa física"
              : "Cadastro de empresa"}
          </div>

          <GoogleAuthButton
            onCredential={handleGoogleCredential}
            disabled={isSubmitting}
            className="mt-5"
          />

          <div className="my-5 flex items-center gap-4">
            <div className="h-px flex-1 bg-slate-200" />

            <span className="text-xs text-slate-400">
              ou continue com e-mail
            </span>

            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="firstName"
                  className="text-sm font-semibold text-slate-800"
                >
                  Nome
                </label>

                <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                  <User size={18} className="text-slate-400" />

                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="João"
                    autoComplete="given-name"
                    disabled={isSubmitting}
                    required
                    className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="text-sm font-semibold text-slate-800"
                >
                  Sobrenome
                </label>

                <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                  <User size={18} className="text-slate-400" />

                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Silva"
                    autoComplete="family-name"
                    disabled={isSubmitting}
                    required
                    className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label
                htmlFor="email"
                className="text-sm font-semibold text-slate-800"
              >
                E-mail
              </label>

              <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                <Mail size={18} className="text-slate-400" />

                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  autoComplete="email"
                  disabled={isSubmitting}
                  required
                  className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div className="mt-4">
              <label
                htmlFor="password"
                className="text-sm font-semibold text-slate-800"
              >
                Senha
              </label>

              <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                <LockKeyhole size={18} className="text-slate-400" />

                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Crie uma senha"
                  autoComplete="new-password"
                  minLength={8}
                  disabled={isSubmitting}
                  required
                  className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  disabled={isSubmitting}
                  className="text-slate-400 transition-colors hover:text-blue-600 disabled:cursor-not-allowed"
                  aria-label={
                    showPassword ? "Ocultar senha" : "Mostrar senha"
                  }
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>

              <p className="mt-2 text-xs text-slate-400">
                Mínimo de 8 caracteres.
              </p>
            </div>

            <div className="mt-4">
              <label
                htmlFor="passwordConfirmation"
                className="text-sm font-semibold text-slate-800"
              >
                Confirmar senha
              </label>

              <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                <LockKeyhole size={18} className="text-slate-400" />

                <input
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  type={showPasswordConfirmation ? "text" : "password"}
                  placeholder="Digite a senha novamente"
                  autoComplete="new-password"
                  minLength={8}
                  disabled={isSubmitting}
                  required
                  className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowPasswordConfirmation((current) => !current)
                  }
                  disabled={isSubmitting}
                  className="text-slate-400 transition-colors hover:text-blue-600 disabled:cursor-not-allowed"
                  aria-label={
                    showPasswordConfirmation
                      ? "Ocultar confirmação de senha"
                      : "Mostrar confirmação de senha"
                  }
                >
                  {showPasswordConfirmation ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <label className="mt-5 flex items-start gap-3 text-sm leading-6 text-slate-500">
              <input
                type="checkbox"
                required
                disabled={isSubmitting}
                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 accent-blue-600 disabled:cursor-not-allowed"
              />

              <span>
                Concordo com os{" "}
                <Link
                  href="/termos-de-uso"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Termos de Uso
                </Link>{" "}
                e com a{" "}
                <Link
                  href="/politica-de-privacidade"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Política de Privacidade
                </Link>
                .
              </span>
            </label>

            {errorMessage && (
              <div
                role="alert"
                className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
              >
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-bold text-white shadow-[0_12px_30px_rgba(37,99,235,0.25)] transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <>
                  <LoaderCircle size={18} className="animate-spin" />
                  Criando conta...
                </>
              ) : (
                "Criar conta"
              )}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-500">
            Já tem uma conta?{" "}
            <Link
              href="/login"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Entre aqui
            </Link>
          </p>
        </div>
      </section>

      <aside className="relative hidden min-h-screen w-1/2 overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 lg:flex lg:items-center lg:justify-center">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-28 -left-20 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />

        <div className="relative z-10 max-w-lg px-12 text-center text-white">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-white/15 backdrop-blur-sm">
            <Building2 size={38} />
          </div>

          <h2 className="mt-8 text-4xl font-bold tracking-tight">
            Comece sua loja hoje
          </h2>

          <p className="mt-5 text-lg leading-8 text-blue-50">
            Cadastre seus produtos, receba pedidos pelo WhatsApp e tenha sua
            loja disponível para clientes de todo o Brasil.
          </p>
        </div>
      </aside>
    </div>
  );
}
